/**
 * Layout edits — the only functions that change a room's sections.
 *
 * Each takes a room and returns either a NEW room with the edit applied or the
 * reason it was refused. Nothing is mutated, and nothing invalid ever comes
 * back: every edit is checked against the placement rules in `validation.ts`
 * before it is accepted, whichever part of the designer asked for it.
 */

import { ProductColor } from '../../types';
import { Section, Surface, RoomSurfaces, SectionProductType } from '../../types/sections';
import { SLATWALL_COVERAGE_HEIGHT } from '../constants';
import { Rect, rectOf, intersectRect } from './resolver';
import { checkPlacement, findSection, validateSurface, PlacementIssue, PlacementOptions } from './validation';
import { AccessoryIssue, checkAccessory, findAccessory, fittingAccessories, freeAccessorySpot, nextAccessoryId, resolveAccessoryMove, snapToGroove } from './accessories';
import { SlatWallAccessoryKind } from '../../data/slatwallAccessories';
import { RectPatches } from './arrange';
import { sectionFillMode, replaceRoomSurface, roomSurfaceById } from '../../types/sections';

export type EditFailure = PlacementIssue | AccessoryIssue | 'not-found' | 'duplicate-id' | 'no-room' | 'passage-fixed' | 'straddles-shared-wall' | 'other-room';
export type EditResult = { ok: true; room: RoomSurfaces } | { ok: false; reason: EditFailure };

const fail = (reason: EditFailure): EditResult => ({ ok: false, reason });

const replaceSurface = replaceRoomSurface;
const findSurface = roomSurfaceById;

/**
 * Change a section's placement and/or its options (colour, orientation, …).
 * `productType` is not changed here — use `changeProductType`, which also
 * reshapes the section for the new product.
 */
export function updateSection(
  room: RoomSurfaces,
  surfaceId: string,
  sectionId: string,
  patch: Partial<Omit<Section, 'id' | 'productType'>>,
  options: Omit<PlacementOptions, 'ignoreId'> = {},
): EditResult {
  const found = findSection(room, surfaceId, sectionId);
  if (!found) return fail('not-found');
  const moved: Section = { ...found.section, ...patch };
  const issue = checkPlacement(found.surface, rectOf(moved), moved.productType, { ...options, ignoreId: sectionId });
  if (issue) return fail(issue);
  // A section's accessories go with it; any a resize leaves hanging off it are dropped.
  const next = withFittingAccessories(found.surface, moved);
  return {
    ok: true,
    room: replaceSurface(room, {
      ...found.surface,
      sections: found.surface.sections.map((s) => (s.id === sectionId ? next : s)),
    }),
  };
}

/** `section` with only the accessories that fit it on `surface` (none at all on anything but SlatWall). */
function withFittingAccessories(surface: Surface, section: Section): Section {
  if (!section.accessories) return section;
  const kept = fittingAccessories(surface, section);
  if (kept === section.accessories) return section;
  const next = { ...section };
  if (kept) next.accessories = kept;
  else delete next.accessories;
  return next;
}

export function addSection(room: RoomSurfaces, surfaceId: string, section: Section): EditResult {
  const surface = findSurface(room, surfaceId);
  if (!surface) return fail('not-found');
  if (surface.sections.some((s) => s.id === section.id)) return fail('duplicate-id');
  const issue = checkPlacement(surface, rectOf(section), section.productType);
  if (issue) return fail(issue);
  return { ok: true, room: replaceSurface(room, { ...surface, sections: [...surface.sections, section] }) };
}

/**
 * Add several sections in ONE step (one undo) — a quick layout's stacked bands.
 * Each is checked against the surface with the ones before it already on it,
 * so the bands may not overlap each other either. If any is refused, nothing
 * is added.
 */
export function addSections(room: RoomSurfaces, surfaceId: string, sections: Section[]): EditResult {
  let current = room;
  for (const section of sections) {
    const result = addSection(current, surfaceId, section);
    if (!result.ok) return result;
    current = result.room;
  }
  return { ok: true, room: current };
}

// ---------------------------------------------------------------- SlatWall accessories

export type AccessoryAddResult = { ok: false; reason: EditFailure } | { ok: true; room: RoomSurfaces; accessoryId: string };

/** Hang a new accessory of `kind` on a SlatWall section, at the first free spot. */
export function addAccessory(room: RoomSurfaces, surfaceId: string, sectionId: string, kind: SlatWallAccessoryKind): AccessoryAddResult {
  const found = findSection(room, surfaceId, sectionId);
  if (!found) return { ok: false, reason: 'not-found' };
  const { surface, section } = found;
  if (section.productType !== 'SlatWall') return { ok: false, reason: 'not-slatwall' };
  const spot = freeAccessorySpot(surface, section, kind);
  if (!spot) return { ok: false, reason: 'no-room' };
  const accessory = { id: nextAccessoryId(surface, section), kind, ...spot };
  const next: Section = { ...section, accessories: [...(section.accessories ?? []), accessory] };
  return {
    ok: true,
    accessoryId: accessory.id,
    room: replaceSurface(room, { ...surface, sections: surface.sections.map((s) => (s.id === sectionId ? next : s)) }),
  };
}

/** Move an accessory to (x, y) on its section (section-relative; the top is snapped to a slot). */
export function moveAccessory(room: RoomSurfaces, surfaceId: string, accessoryId: string, x: number, y: number): EditResult {
  const surface = findSurface(room, surfaceId);
  const found = surface && findAccessory(surface, accessoryId);
  if (!surface || !found) return fail('not-found');
  const { section, accessory } = found;
  const snapped = snapToGroove(section, accessory.kind, y);
  const issue = checkAccessory(surface, section, accessory.kind, x, snapped, accessoryId);
  if (issue) return fail(issue);
  const next: Section = { ...section, accessories: section.accessories!.map((a) => (a.id === accessoryId ? { ...a, x, y: snapped } : a)) };
  return { ok: true, room: replaceSurface(room, { ...surface, sections: surface.sections.map((s) => (s.id === section.id ? next : s)) }) };
}

/** Move an accessory by (dx, dy) feet, as far as the rules let it go (a drag or a nudge). */
export function nudgeAccessory(room: RoomSurfaces, surfaceId: string, accessoryId: string, dx: number, dy: number): EditResult {
  const surface = findSurface(room, surfaceId);
  const found = surface && findAccessory(surface, accessoryId);
  if (!surface || !found) return fail('not-found');
  const to = resolveAccessoryMove(surface, found.section, found.accessory, dx, dy);
  if (!to) return fail('overlap');
  return moveAccessory(room, surfaceId, accessoryId, to.x, to.y);
}

/**
 * Move several accessories of one surface at once — align / distribute. The
 * patches are in SURFACE coordinates (as the arrange maths gives them); each
 * accessory's top is snapped to a slot. The rules are checked with all of
 * them moved: if any lands off its section, over another, or over an opening,
 * nothing is changed.
 */
export function arrangeAccessories(room: RoomSurfaces, surfaceId: string, patches: RectPatches): EditResult {
  const surface = findSurface(room, surfaceId);
  if (!surface) return fail('not-found');
  const moved: Section[] = surface.sections.map((section) => {
    if (!section.accessories?.some((a) => patches[a.id])) return section;
    return {
      ...section,
      accessories: section.accessories.map((a) => {
        const p = patches[a.id];
        if (!p) return a;
        const x = p.x !== undefined ? p.x - section.x : a.x;
        const y = snapToGroove(section, a.kind, p.y !== undefined ? p.y - section.y : a.y);
        return { ...a, x, y };
      }),
    };
  });
  const next: Surface = { ...surface, sections: moved };
  for (const section of moved) {
    for (const a of section.accessories ?? []) {
      if (!patches[a.id]) continue;
      const issue = checkAccessory(next, section, a.kind, a.x, a.y, a.id);
      if (issue) return fail(issue);
    }
  }
  return { ok: true, room: replaceSurface(room, next) };
}

export function deleteAccessory(room: RoomSurfaces, surfaceId: string, accessoryId: string): EditResult {
  const surface = findSurface(room, surfaceId);
  const found = surface && findAccessory(surface, accessoryId);
  if (!surface || !found) return fail('not-found');
  const { section } = found;
  const left = section.accessories!.filter((a) => a.id !== accessoryId);
  const next: Section = { ...section };
  if (left.length) next.accessories = left;
  else delete next.accessories;
  return { ok: true, room: replaceSurface(room, { ...surface, sections: surface.sections.map((s) => (s.id === section.id ? next : s)) }) };
}

export function deleteSection(room: RoomSurfaces, surfaceId: string, sectionId: string): EditResult {
  const found = findSection(room, surfaceId, sectionId);
  if (!found) return fail('not-found');
  return {
    ok: true,
    room: replaceSurface(room, {
      ...found.surface,
      sections: found.surface.sections.filter((s) => s.id !== sectionId),
    }),
  };
}

/** Whole 6" rows that fit in `height` (at least one). */
function wholeRows(height: number): number {
  return Math.max(1, Math.floor(height / SLATWALL_COVERAGE_HEIGHT + 1e-6)) * SLATWALL_COVERAGE_HEIGHT;
}

/**
 * Switch a section between W&CB and SlatWall, keeping its place on the surface.
 * Going to SlatWall trims the height down to whole 6" rows (bottom edge stays)
 * and drops the W&CB-only options; going to W&CB starts horizontal. Trim chosen
 * for its sides is kept.
 */
export function changeProductType(
  room: RoomSurfaces,
  surfaceId: string,
  sectionId: string,
  productType: SectionProductType,
  color: ProductColor,
): EditResult {
  const found = findSection(room, surfaceId, sectionId);
  if (!found) return fail('not-found');
  const { section, surface } = found;
  const edges = section.edges ? { edges: section.edges } : {};
  const next: Section = productType === 'SlatWall'
    ? { id: section.id, productType, x: section.x, y: section.y, width: section.width, height: wholeRows(section.height), color, ...edges }
    : { id: section.id, productType, x: section.x, y: section.y, width: section.width, height: section.height, color, orientation: section.orientation ?? 'Horizontal', ...edges };
  const issue = checkPlacement(surface, rectOf(next), productType, { ignoreId: sectionId });
  if (issue) return fail(issue);
  return {
    ok: true,
    room: replaceSurface(room, { ...surface, sections: surface.sections.map((s) => (s.id === sectionId ? next : s)) }),
  };
}

/** A section id not yet used on the surface, e.g. `wall1-wcb-2`. */
export function nextSectionId(surface: Surface, productType: SectionProductType): string {
  const stem = `${surface.id}-${productType === 'SlatWall' ? 'slatwall' : 'wcb'}`;
  const used = new Set(surface.sections.map((s) => s.id));
  let n = surface.sections.length + 1;
  while (used.has(`${stem}-${n}`)) n++;
  return `${stem}-${n}`;
}

/**
 * A new section sized to fill a bare area of the surface (see `computeGaps`).
 * SlatWall takes as many whole rows as fit, from the bottom of the area.
 */
export function sectionForGap(
  surface: Surface,
  gap: Rect,
  productType: SectionProductType,
  color: ProductColor,
): Section {
  const id = nextSectionId(surface, productType);
  if (productType === 'SlatWall') {
    return { id, productType, x: gap.x, y: gap.y, width: gap.width, height: wholeRows(gap.height), color };
  }
  return { id, productType, x: gap.x, y: gap.y, width: gap.width, height: gap.height, color, orientation: 'Horizontal' };
}

/**
 * Move / resize several sections of one surface in a single step — what align
 * and distribute need. The rules are checked against the surface as it would be
 * AFTERWARDS, all sections moved; checking them one at a time would refuse a
 * swap that is perfectly fine as a whole. If anything breaks a rule, nothing
 * is changed.
 */
export function updateSections(
  room: RoomSurfaces,
  surfaceId: string,
  patches: Record<string, Partial<Rect>>,
): EditResult {
  const surface = findSurface(room, surfaceId);
  if (!surface) return fail('not-found');
  if (Object.keys(patches).some((id) => !surface.sections.some((s) => s.id === id))) return fail('not-found');
  const next: Surface = {
    ...surface,
    sections: surface.sections.map((s) => (patches[s.id] ? { ...s, ...patches[s.id] } : s)),
  };
  const issue = validateSurface(next).find((i) => patches[i.sectionId]) ?? validateSurface(next)[0];
  if (issue) return fail(issue.issue);
  return { ok: true, room: replaceSurface(room, next) };
}

/** Remove several sections of one surface in a single step. */
export function deleteSections(room: RoomSurfaces, surfaceId: string, sectionIds: string[]): EditResult {
  const surface = findSurface(room, surfaceId);
  if (!surface) return fail('not-found');
  const doomed = new Set(sectionIds);
  if (sectionIds.some((id) => !surface.sections.some((s) => s.id === id))) return fail('not-found');
  return { ok: true, room: replaceSurface(room, { ...surface, sections: surface.sections.filter((s) => !doomed.has(s.id)) }) };
}

/**
 * Where a copy of `section` could go: the free spot nearest the original.
 *
 * "Free" follows the overlap rules. A copy of a FILL (Wall&CeilingBoard) needs
 * genuinely bare wall — clear of every other section. A copy of an INSERT
 * (SlatWall) only has to stay clear of other inserts; it may sit over boards,
 * which are carved around it like any other inset.
 *
 * Free space always touches something, so the candidates tried are the
 * positions that put the copy flush against the surface's edges or against
 * another section's edge, in the original's own row and column as well as
 * everywhere else. A spot beside the original (same row) is preferred, then
 * one directly above or below it, then the nearest anywhere.
 */
export function findFreeSpot(surface: Surface, section: Section): Rect | null {
  const { width, height } = section;
  if (width > surface.width + 1e-9 || height > surface.height + 1e-9) return null;
  const isFill = sectionFillMode(section.productType) === 'fill';
  const blockers = surface.sections
    .filter((s) => isFill || sectionFillMode(s.productType) === 'insert')
    .map(rectOf);

  const xs = new Set<number>([0, surface.width - width, section.x]);
  const ys = new Set<number>([0, surface.height - height, section.y]);
  for (const b of surface.sections.map(rectOf)) {
    xs.add(b.x + b.width); xs.add(b.x - width);
    ys.add(b.y + b.height); ys.add(b.y - height);
  }

  let best: Rect | null = null;
  let bestDist = Infinity;
  xs.forEach((x) => {
    if (x < -1e-9 || x + width > surface.width + 1e-9) return;
    ys.forEach((y) => {
      if (y < -1e-9 || y + height > surface.height + 1e-9) return;
      const rect = { x: Math.max(0, x), y: Math.max(0, y), width, height };
      if (blockers.some((b) => intersectRect(rect, b))) return;
      const sameRow = Math.abs(y - section.y) < 1e-9;
      const sameColumn = Math.abs(x - section.x) < 1e-9;
      const rank = sameRow ? 0 : sameColumn ? 1 : 2;
      const dist = rank * 1e6 + Math.abs(x - section.x) + Math.abs(y - section.y);
      if (dist < bestDist) { bestDist = dist; best = rect; }
    });
  });
  return best;
}

export type DuplicateResult = { ok: true; room: RoomSurfaces; sectionId: string } | { ok: false; reason: EditFailure };

/** Copy a section into the nearest free spot on its surface; `no-room` when there isn't one. */
export function duplicateSection(room: RoomSurfaces, surfaceId: string, sectionId: string): DuplicateResult {
  const found = findSection(room, surfaceId, sectionId);
  if (!found) return { ok: false, reason: 'not-found' };
  const spot = findFreeSpot(found.surface, found.section);
  if (!spot) return { ok: false, reason: 'no-room' };
  const copy: Section = { ...found.section, ...spot, id: nextSectionId(found.surface, found.section.productType) };
  const added = addSection(room, surfaceId, copy);
  return added.ok ? { ok: true, room: added.room, sectionId: copy.id } : added;
}
